import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { API } from '../config/apiConfig';

function VideoEdit() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    channelId: '',
    visibility: 'public',
  });

  const [categories, setCategories] = useState([]);
  const [userChannels, setUserChannels] = useState([]);

  useEffect(() => {
    fetchData();
  }, [videoId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [videoRes, categoriesRes, channelsRes] = await Promise.all([
        axios.get(API.videoById(videoId), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(API.videoCategories),
        axios.get(API.myChannels, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (videoRes.data.success && videoRes.data.video) {
        const video = videoRes.data.video;
        setFormData({
          title: video.Title || '',
          description: video.VideoDescription || '',
          categoryId: video.VideoCategoryId || '',
          channelId: video.ChannelId || '',
          visibility: video.Visibility || 'public',
        });
      }

      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.categories || []);
      }

      if (channelsRes.data.success) {
        setUserChannels(channelsRes.data.channels || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load video data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await axios.put(
        API.updateVideo(videoId),
        {
          title: formData.title,
          description: formData.description,
          categoryId: formData.categoryId,
          channelId: formData.channelId,
          visibility: formData.visibility,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        alert('Video updated successfully!');
        navigate(-1); // Go back
      }
    } catch (err) {
      console.error('Error updating video:', err);
      alert(err.response?.data?.error || 'Failed to update video');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit Video
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            required
            label="Video Title"
            value={formData.title}
            onChange={handleChange('title')}
            disabled={saving}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            disabled={saving}
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.categoryId}
              label="Category"
              onChange={handleChange('categoryId')}
              disabled={saving}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.VideoCategoryId} value={cat.VideoCategoryId}>
                  {cat.Category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={formData.channelId}
              label="Channel"
              onChange={handleChange('channelId')}
              disabled={saving}
            >
              {userChannels.map((channel) => (
                <MenuItem key={channel.ChannelId} value={channel.ChannelId}>
                  {channel.ChannelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 3 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={formData.visibility}
              label="Visibility"
              onChange={handleChange('visibility')}
              disabled={saving}
            >
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="unlisted">Unlisted</MenuItem>
            </Select>
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
            fullWidth
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default VideoEdit;

